import type { LocalizedTemplateWithValues } from '@libs/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useTrans } from '@libs/i18n';

import { apiClient } from '../../../api/client';
import { queryKeys } from '../../../constants/query-keys';

import type {
  ActiveTemplateCategory,
  ActiveTemplateOutline,
  TenantProfileVersionDto,
  VersionRef,
} from './types';
import { useBackendErrorToast } from './useBackendErrorToast';

async function fetchTemplateOutline(): Promise<ActiveTemplateOutline> {
  const { data } = await apiClient.get<ActiveTemplateOutline>(
    'api/tenant-profile/template/active/categories',
  );
  return data;
}

async function fetchCategoryWithValues(
  categoryId: string,
): Promise<ActiveTemplateCategory> {
  const { data } = await apiClient.get<ActiveTemplateCategory>(
    `api/tenant-profile/template/active/categories/${categoryId}`,
  );
  return data;
}

/**
 * The template's labels/descriptions are localized server-side from the
 * request's `Accept-Language` (set by the API client from i18next). Keying the
 * outline + per-category queries by the active language makes React Query
 * refetch — and re-localize — on a language switch. Mutations still invalidate
 * via the static prefixes (`templateOutline` / `templateCategory`), which match
 * regardless of locale.
 */
export const useTemplateOutline = () => {
  const { i18n } = useTrans();
  return useQuery({
    queryKey: [...queryKeys.tenantProfile.templateOutline, i18n.language],
    queryFn: fetchTemplateOutline,
  });
};

async function fetchActiveTemplateWithValues(): Promise<LocalizedTemplateWithValues> {
  const { data } = await apiClient.get<LocalizedTemplateWithValues>(
    'api/tenant-profile/template/active',
  );
  return data;
}

/**
 * The full active template with the tenant's current values overlaid. Each
 * field carries its `consumers` (the standard documents it feeds) and
 * `currentValue`, which lets the UI derive per-domain field needs client-side
 * without a dedicated endpoint. Keyed by language so it re-localizes on switch.
 */
export const useActiveTemplateWithValues = () => {
  const { i18n } = useTrans();
  return useQuery({
    queryKey: [...queryKeys.tenantProfile.activeTemplate, i18n.language],
    queryFn: fetchActiveTemplateWithValues,
  });
};

/** Lazy-loads a single category's fields + values; disabled until an id is set. */
export const useCategoryWithValues = (categoryId: string | null) => {
  const { i18n } = useTrans();
  return useQuery({
    queryKey: [
      ...queryKeys.tenantProfile.templateCategory(categoryId ?? ''),
      i18n.language,
    ],
    queryFn: () => fetchCategoryWithValues(categoryId as string),
    enabled: !!categoryId,
  });
};

async function applyManualEdits(
  fieldValues: Record<string, unknown>,
): Promise<VersionRef> {
  const { data } = await apiClient.patch<VersionRef>(
    'api/tenant-profile/manual-edits',
    { fieldValues },
  );
  return data;
}

/**
 * Manual edits are synchronous: the mutation mints a TenantProfileVersion in
 * one request and returns its ref. Refresh the template values + version list
 * on success. A concurrent save of the same category fails with a 409
 * (`TENANT_PROFILE_CATEGORY_LOCKED`) surfaced via the mutation error.
 */
export const useApplyManualEdits = () => {
  const queryClient = useQueryClient();
  const onError = useBackendErrorToast('app.orgProfile.banner.saveFailed');
  return useMutation({
    mutationFn: applyManualEdits,
    onError,
    onSuccess: () => {
      // Counts change (outline) and the edited category's values change; the
      // category-key prefix refetches whichever category is currently mounted.
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.templateOutline,
      });
      queryClient.invalidateQueries({
        queryKey: ['tenantProfile', 'template', 'category'],
      });
      // The whole-template-with-values read is a sibling key, not a child of
      // either prefix above, so it survived every save — a caller that reads
      // the profile through it (the remediation placeholders step) kept seeing
      // the pre-save `currentValue` and asking for a blank it had just filled.
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.activeTemplate,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.versions,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantProfile.summary,
      });
    },
  });
};

async function fetchVersions(): Promise<TenantProfileVersionDto[]> {
  const { data } = await apiClient.get<TenantProfileVersionDto[]>(
    'api/tenant-profile/versions',
  );
  return data;
}

export const useListTenantProfileVersions = () =>
  useQuery({
    queryKey: queryKeys.tenantProfile.versions,
    queryFn: fetchVersions,
  });
